import Access from 'ui/Access';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import DetailsButton from 'ui/DetailsButton';
import { ERROR_RESET_CONFIRMATION_NEEDED } from '../../../ApiError';
import EditButton from 'ui/EditButton';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import MultipleUsersForm from './MultipleUsersForm';
import React from 'react';
import ResetPasswordDialog from 'ui/dialog/ResetPasswordDialog';
import Stats from '../../../ui/stats/Stats';
import StatusChip from 'ui/StatusChip';
import StatusIcon from 'ui/StatusIcon';
import UserForm from './UserForm';
import UserInfo from 'ui/user/UserInfo';
import VirtualizedTable from '../../../ui/mtablevirtualized/VirtualizedTable';
import WhiteBack from '../../WhiteBack';
import autoTable from 'jspdf-autotable';
import formatters from '../../../formatters';
import { groupBy } from 'utils/collections';
import { userActions } from './types';
import { withAppContext } from '../../AppContext';

class Users extends React.Component {
    constructor(props) {
        super(props);
        this.childRef = React.createRef();
    }

    state = {
        selectedUsers: [],
        currentAction: null,
        actionResultName: null,
        pendingResetOptions: null,
    };

    componentDidUpdate = (prevProps, prevState) => {
        const hadError =
            prevProps.formErrors !== this.props.formErrors &&
            this.props.formErrors === null;
        const withoutError =
            this.props.formErrors === null && prevState.currentAction !== null;
        if (!this.props.currentUser && (withoutError || hadError)) {
            this.setState({ currentAction: null });
            this.props.onReset();
        }
    };

    resetSelectedUsers = () => {
        if (this.childRef.current) {
            this.childRef.current.resetSelectedRows();
        }
    };

    getAvailableActions = (selectedUsers, actions) => {
        const managed = Object.keys(groupBy(selectedUsers, 'managed'));
        const managedBy = Object.keys(groupBy(selectedUsers, 'managed_by'));
        let selectedStatuses = Object.keys(groupBy(selectedUsers, 'status'));
        selectedStatuses = selectedStatuses.concat(managed, managedBy).sort();
        selectedStatuses = new Set(selectedStatuses);

        const actionFilters = action => {
            if (action.single && selectedUsers.length > 1) {
                return false;
            }
            if (action.actionFilter) {
                return selectedUsers.every(user => action.actionFilter(user));
            }
            return true;
        };
        return actions
            .filter(action => {
                const some = action.notAllowedUsersStatuses.filter(status => {
                    return selectedStatuses.has(status);
                });
                return some.length === 0;
            })
            .filter(actionFilters);
    };

    getColumns = editable => {
        return [
            {
                accessorKey: 'uuid',
                header: 'Uuid',
                isVisible: false,
                field: 'uuid',
                title: 'Uuid',
                notFilter: true,
                hidden: true,
                export: false,
            },
            {
                accessorKey: 'email',
                header: 'User',
                Cell: ({ cell }) => <UserInfo user={cell.row.original} />,
                size: 350,
                field: 'email',
                title: 'User',
                defaultSort: 'asc',
                cellStyle: {
                    minWidth: '10%',
                },
            },
            {
                accessorKey: 'groups',
                header: 'Groups',
                Cell: ({ cell }) => {
                    const names = cell.row.original.groups
                        .map(group => group.name)
                        .join(', ');
                    return <span>{names}</span>;
                },
                field: 'groups',
                title: 'Groups',
                filter: (value = [], filter) => {
                    return value.find(item => {
                        return filter.value.some(f =>
                            item.name.toLowerCase().includes(f.toLowerCase())
                        );
                    });
                },
                exportValue: val => val.map(group => group.name).join(', '),
                cellStyle: {
                    width: '300px',
                },
            },
            {
                accessorKey: 'role',
                header: 'Role',
                size: 100,
                Cell: ({ cell }) => {
                    if (cell.row.original.role === 'Individual') {
                        return <span>User</span>;
                    }
                    return <span>{cell.row.original.role}</span>;
                },
                field: 'role',
                title: 'Role',
                cellStyle: {
                    width: '100px',
                },
                filterType: 'select',
            },
            {
                accessorKey: 'security_score_business',
                header: 'Security Score',
                Cell: ({ cell }) => {
                    const val = cell.row.original.security_score_business || 0;
                    return <span>{Number.parseFloat(val).toFixed()}</span>;
                },
                field: 'security_score_business',
                title: 'Security Score',
                filter: (value = 0, filter) =>
                    Number.parseFloat(value).toFixed() ===
                    Number.parseFloat(filter.value).toFixed(),
                exportValue: val => val || '',
                cellStyle: {
                    width: '75px',
                },
            },
            {
                accessorKey: 'secure_items',
                header: 'Items',
                size: 80,
                field: 'secure_items',
                title: 'Items',
                filter: (value, filter) => value === Number(filter.value),
                exportValue: val => val || '',
                cellStyle: {
                    minWidth: '50px',
                },
            },
            {
                accessorKey: 'multi_factor_authentication',
                header: '2 Factor Auth',
                size: 140,
                Cell: ({ cell }) => {
                    return (
                        <StatusIcon
                            active={
                                cell.row.original.multi_factor_authentication
                            }
                        />
                    );
                },
                field: 'multi_factor_authentication',
                title: '2 Factor Auth',
                filter: (value, filter) => value === filter.value,
                exportValue: val => val || '',
                filterType: 'boolean',
                cellStyle: {
                    minWidth: '140px',
                },
            },
            {
                accessorKey: 'last_device_sync',
                header: 'Last Sync',
                Cell: ({ cell }) =>
                    formatters.date(cell.row.original.last_device_sync),
                field: 'last_device_sync',
                title: 'Last Sync',
                cellStyle: {
                    minWidth: '170px',
                },
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 140,
                Cell: ({ cell }) => {
                    if (cell.row.original.status === 'active') {
                        return (
                            <StatusChip
                                label={cell.row.original.status_name}
                                variant="active"
                            />
                        );
                    } else if (
                        cell.row.original.status === 'disabled' ||
                        cell.row.original.status === 'pending_approval'
                    ) {
                        return (
                            <StatusChip
                                label={cell.row.original.status_name}
                                variant="disabled"
                            />
                        );
                    }
                    return (
                        <StatusChip
                            label={cell.row.original.status_name}
                            variant="default"
                        />
                    );
                },
                field: 'status',
                title: 'Status',
                cellStyle: {
                    width: '150px',
                },
                filterType: 'select',
                exportValue: (val, row) => row.status_name,
            },
            {
                accessorKey: 'managed_by',
                header: 'Managed By',
                size: 140,
                Cell: ({ cell }) => {
                    const label = formatters.managedByLabel(
                        cell.row.original.managed_by
                    );
                    if (!cell.row.original.managed) {
                        return <span style={{ color: 'red' }}>{label}</span>;
                    }
                    return <span>{label}</span>;
                },
                exportLabel: val => formatters.managedByLabel(val),
                field: 'managed_by',
                title: 'Managed By',
                filterType: 'select',
                cellStyle: {
                    width: '125px',
                },
            },
            {
                accessorKey: 'action',
                header: 'Action',
                size: 100,
                notFilter: true,
                export: false,
                Cell: ({ cell }) => {
                    return this.isSelectable(editable, cell.row.original) ? (
                        <EditButton
                            onClick={this.handleEdit(cell.row.original)}
                        />
                    ) : (
                        <DetailsButton
                            onClick={this.handleDetails(cell.row.original)}
                        />
                    );
                },
                field: 'action',
                title: '',
                notFilter: true,
                export: false,
                cellStyle: {
                    width: '120px',
                },
            },
        ];
    };

    handleAction = currentAction => selectedUsers => {
        if (currentAction === userActions.APPROVE_PENDING) {
            if (selectedUsers.length > this.props.stats[1].value) {
                currentAction = userActions.APPROVE_PENDING_INVALID;
            }
        }

        if (currentAction === userActions.DELETE_USERS) {
            currentAction = this.props.validDeleteUsers(selectedUsers);
        }
        this.setState({
            currentAction,
            selectedUsers,
        });
    };

    handleCloseDialog = () => () => {
        this.setState({ currentAction: null });
    };

    handleOkDialog = ({ callback, actionResultName }) => () => {
        this.setState({ currentAction: null, actionResultName }, () => {
            callback(this.state.selectedUsers, Boolean(actionResultName));
            this.resetSelectedUsers();
        });
    };

    handleAddUser = () => {
        this.setState({ currentAction: userActions.ADD_USER });
    };

    handleAddUsers = () => {
        this.setState({ currentAction: userActions.ADD_USERS });
    };

    getAddUsersActions = dropdownCloseCallback => (
        <Access.Change permission={this.props.permissions.users}>
            <MenuList>
                <MenuItem
                    onClick={() => {
                        this.handleAddUser();
                        dropdownCloseCallback();
                    }}
                >
                    Add single user
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        this.handleAddUsers();
                        dropdownCloseCallback();
                    }}
                >
                    Add multiple users
                </MenuItem>
            </MenuList>
        </Access.Change>
    );

    handleEdit = user => () => {
        this.setState(
            { currentAction: userActions.EDIT_USER },
            this.props.onFetchUser(user)
        );
    };

    handleDetails = user => () => {
        this.setState(
            { currentAction: userActions.USER_DETAILS },
            this.props.onFetchUser(user)
        );
    };

    handleResetUsersPasswordConfirm = (
        selectedUsers,
        usersShowActionResult
    ) => {
        const resetOption = this.state.pendingResetOptions || {};
        const options = { ...resetOption, force: true };
        this.setState({ pendingResetOptions: null });
        return this.props.onResetUsersPassword(
            selectedUsers,
            usersShowActionResult,
            options,
            this.resetSelectedUsers
        );
    };

    renderDialog = () => {
        const { currentAction } = this.state;
        const { currentUser, stats } = this.props;
        const availableLicenses = stats[1].value ?? 0;
        const isSubscriptionPlan =
            this.props.permissions.users === Access.SUBSCRIPTION_USERS;
        const dialog = {
            [userActions.DISABLE_USERS]: {
                title: 'Disable user',
                subtitle1: 'Are you sure you want to disable:',
            },
            [userActions.TRANSFER_PRIMARY_ACCOUNT]: {
                title: 'Transfer primary user account',
                subtitle1:
                    'Do you want to transfer the primary user account role for this company to the following user:',
            },
            [userActions.ENABLE_USERS]: {
                title: 'Enable user',
                subtitle1: 'Are you sure you want to enable:',
            },
            [userActions.DELETE_USERS]: {
                title: 'Delete user',
                subtitle1:
                    'Deleting an account will immediately delete all of the users saved passwords and data. This process cannot be reversed.',
                warning: 'Are you sure you want to delete:',
            },
            [userActions.APPROVE_PENDING]: {
                title: 'Approve accounts',
                subtitle1:
                    'When pending accounts are approved, the users will receive an email with login instructions so they can begin using their account.',
                subtitle2: 'Approve the following accounts?',
            },
            [userActions.CONVERT_TO_PERSONAL]: {
                title: 'Covert to personal account',
                subtitle1:
                    'When an account is converted to a personal account the user(s) will be removed from your subscription and will be placed on an individual 30-day trial subscription.',
                subtitle2: (
                    <>
                        {!isSubscriptionPlan && (
                            <strong>
                                Note: Please review the Security Policies tab
                                before making this change. Depending on how your
                                organization has configured security policies,{' '}
                                <u>some items may be deleted</u> from the user's
                                account when this change is made
                            </strong>
                        )}
                        <p>Convert the account(s) to personal accounts?</p>
                    </>
                ),
            },
            [userActions.REQUIRE_PASSWORD_CHANGE]: {
                title: 'Require password change',
                subtitle1:
                    'Are you sure you want to require a password change for:',
            },
            [userActions.MANAGE_IN_PASSWORDBOSS]: {
                title: 'Manage in CyberFOX Password Manager',
                subtitle1: 'That connector was removed from your account.',
                subtitle2:
                    'If you will be re-adding the connector do not make any changes now. If you will not be re-adding the connector you should convert the user(s) to be managed by CyberFOX Password Manager, which will allow you to manage them via this website.',

                cancelButtonName: 'Do nothing',
                okButtonName: 'Manage in CyberFOX Password Manager',
            },
            [userActions.RESET_PASSWORD_CONFIRMATION]: {
                title: 'Reset Master Password',
                subtitle1: (
                    <>
                        Warning: You’re about to remove the last account(s) from
                        the recovery group. To avoid losing access to your
                        company’s backup files, we recommend either:
                        <ul style={{ marginTop: 5, marginBottom: 0 }}>
                            <li>
                                Adding another member to the recovery group
                                before continue.
                            </li>
                            <li>
                                Downloading the Business Key PDF before
                                proceeding.
                            </li>
                        </ul>
                        Continuing without taking action may put your backups at
                        risk.
                    </>
                ),
                subtitle2: 'Are you sure you want to continue?:',
                okButtonName: 'Confirm',
            },
        };

        const callbacks = {
            [userActions.DISABLE_USERS]: {
                callback: this.props.onDisableUsers,
            },
            [userActions.TRANSFER_PRIMARY_ACCOUNT]: {
                callback: this.props.onTransferPrimaryAccount,
            },
            [userActions.ENABLE_USERS]: { callback: this.props.onEnableUsers },
            [userActions.DELETE_USERS]: { callback: this.props.onDeleteUsers },
            [userActions.APPROVE_PENDING]: {
                callback: this.props.onApprovePendingUsers,
            },
            [userActions.CONVERT_TO_PERSONAL]: {
                callback: this.props.onConvertToPersonalAccount,
            },
            [userActions.REQUIRE_PASSWORD_CHANGE]: {
                callback: this.props.onRequireUsersPasswordChange,
                actionResultName: userActions.RESET_PASSWORD_SUCCESS,
            },
            [userActions.RESET_PASSWORD]: {
                callback: this.props.onResetUsersPassword,
                actionResultName: userActions.RESET_PASSWORD_SUCCESS,
            },
            [userActions.MANAGE_IN_PASSWORDBOSS]: {
                callback: this.props.onManageUsersInPasswordBoss,
            },
            [userActions.RESET_PASSWORD_CONFIRMATION]: {
                callback: this.handleResetUsersPasswordConfirm,
                actionResultName: userActions.RESET_PASSWORD_SUCCESS,
            },
        };

        const errorDialogs = {
            [userActions.APPROVE_PENDING_INVALID]: {
                title: 'License count exceeded',
                subtitle1:
                    'The number of pending user accounts you have chosen to active exceeds the available licenses on your account. You will need to either purchase additional user licenses or remove some existing users from your account.',
            },
            [userActions.DELETE_ADMINS_INVALID]: {
                title: 'Delete administrators',
                subtitle1:
                    "You can't remove all Administrators. The Company has to have at least one Administrator.",
            },
            [userActions.DELETE_YOURSELF_INVALID]: {
                title: 'Delete user',
                subtitle1: "You can't remove yourself.",
            },
            [userActions.RESET_PASSWORD_ERROR]: {
                title: 'Reset Master Password',
                subtitle1:
                    'An error occurred while resetting the password(s), please try again or contact support.',
            },
        };
        if (errorDialogs[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...errorDialogs[currentAction]}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleCloseDialog(currentAction)}
                    showCancelButton={false}
                />
            );
        }

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={this.state.selectedUsers.map(item => item.email)}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                />
            );
        }

        if (currentAction === userActions.DISABLE_USER_2FA && currentUser) {
            return (
                <ConfirmationDialog
                    open={true}
                    title="Disable 2FA"
                    subtitle1="Disabling 2FA on an account will let a user login to their CyberFOX Password Manager account without entering a 2FA code. Are you sure you want to disable 2FA for this user"
                    data={[currentUser.email]}
                    onCancel={() => {
                        this.setState({
                            currentAction: null,
                        });
                    }}
                    onOk={() => {
                        this.props.onDisableUser2FA(currentUser);
                        this.setState({
                            currentAction: null,
                        });
                    }}
                />
            );
        }

        if (currentAction === userActions.ADD_USER) {
            return (
                <UserForm
                    open={true}
                    mode="new"
                    groups={this.props.groups}
                    formErrors={this.props.formErrors}
                    availableLicenses={availableLicenses}
                    onCancel={() => {
                        this.setState(
                            { currentAction: null },
                            this.props.onFormCancel
                        );
                    }}
                    onOk={userData => this.props.onAddUser(userData)}
                    tabs={this.props.tabs}
                    hideAdmin={isSubscriptionPlan}
                    loggedInUser={this.props.loggedInUser}
                />
            );
        }

        if (currentAction === userActions.ADD_USERS) {
            return (
                <MultipleUsersForm
                    open={true}
                    formErrors={this.props.formErrors}
                    availableLicenses={availableLicenses}
                    currentUsersEmails={this.props.users.map(
                        ({ email }) => email
                    )}
                    onCancel={() => {
                        this.setState({ currentAction: null });
                    }}
                    onOk={usersData => this.props.onAddUsers(usersData)}
                    hideAdmin={isSubscriptionPlan}
                />
            );
        }

        if (currentAction === userActions.EDIT_USER && this.props.currentUser) {
            const handleExtraAction = actionName => () => {
                if (actionName === 'disable-2fa') {
                    this.setState({
                        currentAction: userActions.DISABLE_USER_2FA,
                    });
                }
            };
            const canEditAdmin =
                this.props.users.filter(
                    ({ role, uuid }) =>
                        role === 'Administrator' &&
                        uuid !== this.props.currentUser.uuid
                ).length >= 1;

            return (
                <UserForm
                    open={true}
                    currentUser={this.props.currentUser}
                    mode="edit"
                    canEditAdmin={canEditAdmin}
                    groups={this.props.groups}
                    formErrors={this.props.formErrors}
                    availableLicenses={availableLicenses}
                    onCancel={() => {
                        this.setState(
                            { currentAction: null },
                            this.props.onFormCancel
                        );
                    }}
                    okButtonName="Save changes"
                    onOk={userData => this.props.onUpdateUser(userData)}
                    onDelete={() => {
                        this.handleAction(userActions.DELETE_USERS)([
                            this.props.currentUser,
                        ]);
                    }}
                    extraAction={handleExtraAction}
                    tabs={this.props.tabs}
                    hideAdmin={isSubscriptionPlan}
                    loggedInUser={this.props.loggedInUser}
                />
            );
        }

        if (
            currentAction === userActions.USER_DETAILS &&
            this.props.currentUser
        ) {
            return (
                <UserForm
                    open={true}
                    currentUser={this.props.currentUser}
                    mode="details"
                    groups={this.props.groups}
                    formErrors={this.props.formErrors}
                    availableLicenses={availableLicenses}
                    okButtonName="OK"
                    onOk={() => {
                        this.setState(
                            { currentAction: null },
                            this.props.onFormCancel
                        );
                    }}
                    tabs={this.props.tabs}
                    hideAdmin={isSubscriptionPlan}
                    loggedInUser={this.props.loggedInUser}
                />
            );
        }

        if (currentAction === userActions.RESET_PASSWORD) {
            return (
                <ResetPasswordDialog
                    open={true}
                    onCancel={() => {
                        this.setState({
                            currentAction: null,
                        });
                    }}
                    displayResetOptions={this.state.selectedUsers.every(
                        user => user.pb6_authentication_enabled
                    )}
                    onOk={options => {
                        this.setState(
                            {
                                currentAction: null,
                                actionResultName:
                                    userActions.RESET_PASSWORD_SUCCESS,
                            },
                            async () => {
                                try {
                                    await this.props.onResetUsersPassword(
                                        this.state.selectedUsers,
                                        true,
                                        options,
                                        this.resetSelectedUsers
                                    );
                                } catch (error) {
                                    const next =
                                        error.code ===
                                        ERROR_RESET_CONFIRMATION_NEEDED
                                            ? userActions.RESET_PASSWORD_CONFIRMATION
                                            : userActions.RESET_PASSWORD_ERROR;
                                    this.setState({
                                        currentAction: next,
                                        actionResultName: null,
                                        pendingResetOptions: options,
                                    });
                                }
                            }
                        );
                    }}
                    data={this.state.selectedUsers.map(user => user.email)}
                />
            );
        }

        if (currentAction === userActions.RESET_PASSWORD_CONFIRMATION) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={this.state.selectedUsers.map(item => item.email)}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                />
            );
        }
    };

    renderActionResult = () => {
        const dialog = {
            [userActions.RESET_PASSWORD_SUCCESS]: {
                title: 'Reset Master Password',
                subtitle1:
                    'The password for the account(s) has been reset. User(s) will be required to change their password.',
            },
        };
        return (
            <ConfirmationDialog
                open={true}
                {...dialog[this.state.actionResultName]}
                onCancel={() => this.props.onActionResult()}
                onOk={() => this.props.onActionResult()}
                showCancelButton={false}
            />
        );
    };

    getActionMenuItems = selectedUsers => {
        const { actions } = this.props;
        const availableActions = this.getAvailableActions(
            selectedUsers,
            actions
        );

        return availableActions.length > 0
            ? availableActions.map(action => ({
                  key: action.key,
                  name: action.name,
                  onClick: dropdownCloseCallback => {
                      this.handleAction(action.key)(selectedUsers);
                      dropdownCloseCallback();
                  },
              }))
            : [
                  {
                      key: 'no',
                      name: 'No common action',
                      onClick: dropdownCloseCallback => {
                          dropdownCloseCallback();
                      },
                  },
              ];
    };

    isSelectable(editable, row) {
        return (
            editable &&
            row.status !== 'creating_account' &&
            row.status !== 'invited'
        );
    }

    handleSelectionChange = val => {
        this.setState({ selectedUsers: val });
    };

    render() {
        const {
            users,
            stats,
            userLoading,
            showActionResult,
            editable,
        } = this.props;
        const { selectedUsers, currentAction } = this.state;
        const columns = this.getColumns(editable);

        const selectedActions = this.getActionMenuItems(selectedUsers);

        return (
            <>
                {currentAction && this.renderDialog()}
                {showActionResult && this.renderActionResult()}
                <WhiteBack />
                <VirtualizedTable
                    stateKey="Users"
                    sortingKey="email"
                    columns={columns}
                    data={users}
                    loading={userLoading}
                    title={<Stats stats={stats} />}
                    options={{
                        selection: editable,
                        selectionProps: row => ({
                            disabled: !this.isSelectable(editable, row),
                        }),
                    }}
                    canChange={editable}
                    actionMenuItems={selectedActions}
                    addMenuItems={this.getAddUsersActions}
                    onSelectionChange={this.handleSelectionChange}
                    searchPlaceholder="Find users"
                    enableExpanding={false}
                    height={'220px'}
                    ref={this.childRef}
                />
            </>
        );
    }
}

export default withAppContext(Users);
